import { useState, FormEvent, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { isValidEmail } from '@/utils/validation';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Toggle } from '@/components/ui/Toggle';
import { devLog } from '@/utils/logger';
import { getSafeReturnPath } from '@/utils/earlySupporterFormat';
import { useAuthMethods } from '@/hooks/useAuthMethods';
import { MagicLoginPanel } from '@/components/auth/MagicLoginPanel';

/**
 * Login Page
 *
 * User authentication form with Agile Assets branding
 */

export const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  // Where to go after login: the page PrivateRoute bounced from (same-origin paths only), else the dashboard
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const fromQuery = new URLSearchParams(location.search).get('returnTo');
  const afterLoginPath = getSafeReturnPath(fromState) ?? getSafeReturnPath(fromQuery) ?? '/';
  const { login, isAuthenticated, isLoading: authLoading, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { magicLogin } = useAuthMethods();
  const [magicMode, setMagicMode] = useState(false);

  // Guard against the auto-reload wiping typed login input mid-session.
  useUnsavedGuard(email.trim().length > 0 || password.length > 0, 'login');

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(user?.needsPlan && afterLoginPath === '/' ? '/shop' : afterLoginPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, afterLoginPath, user?.needsPlan]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!email || !password) {
      setError(t('auth_login_error_all_fields'));
      return;
    }

    if (!isValidEmail(email)) {
      setError(t('auth_login_error_invalid_email'));
      return;
    }

    setIsLoading(true);
    devLog.log('[Login] Starting login process for:', email, 'rememberMe:', rememberMe);

    try {
      devLog.log('[Login] Calling login function with rememberMe:', rememberMe);
      await login(email, password, rememberMe);
      devLog.log('[Login] Login successful, navigating to', afterLoginPath);
      navigate(afterLoginPath);
    } catch (err) {
      devLog.error('[Login] Login error:', err);
      setError(err instanceof Error ? err.message : t('auth_login_error_generic'));
    } finally {
      setIsLoading(false);
      devLog.log('[Login] Login process complete');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      {/* Login card */}
      <div className="relative bg-white rounded-lg shadow-2xl px-7 pt-6 pb-3 sm:px-8 sm:pt-8 sm:pb-4 w-full max-w-lg flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[35%] sm:max-w-[45%] h-auto"
            onError={(e) => {
              devLog.error('Logo failed to load from:', e.currentTarget.src);
              e.currentTarget.onerror = null;
              // Fallback to text logo
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent && !parent.querySelector('.text-logo')) {
                const textLogo = document.createElement('div');
                textLogo.className = 'text-logo text-4xl font-bold text-bs-secondary';

                // Safe: Create DOM elements instead of innerHTML
                const primaryText = document.createElement('span');
                primaryText.className = 'text-bs-secondary';
                primaryText.textContent = 'Ä';

                const dotsText = document.createElement('span');
                dotsText.className = 'text-xs align-top';
                dotsText.textContent = '••';

                textLogo.appendChild(primaryText);
                textLogo.appendChild(dotsText);
                parent.appendChild(textLogo);
              }
            }}
            onLoad={(e) => {
              devLog.log('Logo loaded successfully from:', e.currentTarget.src);
            }}
          />
        </div>

        {/* Tagline — desktop only */}
        <div className="hidden sm:block text-center mb-4 sm:mb-6">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('auth_login_tagline_creating')} <span className="text-bs-secondary font-bold">{t('auth_login_tagline_brand_value')}</span>.
            <br />
            {t('auth_login_tagline_one_asset')}
            <br />
            {t('auth_login_tagline_content_marketing')} <span className="text-bs-secondary font-bold">{t('auth_login_tagline_next_level')}</span>.
          </p>
        </div>

        {/* Welcome heading */}
        <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 text-center mb-5 sm:mb-8 font-wagner">
          {t('auth_login_heading')}
        </h1>

        {/* Error message */}
        {error && (
          <div
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
            role="alert"
          >
            {error}
          </div>
        )}

        {magicLogin && magicMode ? (
          <MagicLoginPanel initialEmail={email} afterLoginPath={afterLoginPath} onBackToPassword={() => setMagicMode(false)} />
        ) : (
        <>
        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            inputSize="sm"
            labelVisible
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth_login_placeholder_email')}
            disabled={isLoading}
            autoComplete="email"
            required
            label={t('auth_login_label_email')}
            labelAction={
              magicLogin ? (
                <button
                  type="button"
                  onClick={() => { setError(''); setMagicMode(true); }}
                  className="text-[12px] sm:text-sm text-bs-secondary hover:text-secondary-600 hover:underline focus-visible:text-secondary-600 focus-visible:underline focus-visible:outline-none font-semibold whitespace-nowrap transition-colors"
                >
                  {t('auth_magic_open_button')}
                </button>
              ) : undefined
            }
          />

          <div className="relative">
            <Input
              inputSize="sm"
              labelVisible
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth_login_placeholder_password')}
              disabled={isLoading}
              autoComplete="current-password"
              required
              label={t('auth_login_label_password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 bottom-0 h-[34px] lg:h-11 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <span className="material-symbols-outlined !text-[18px]">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>

          <div className="flex items-center">
            <Toggle
              label={t('auth_login_remember_me')}
              checked={rememberMe}
              onChange={setRememberMe}
            />
          </div>

          <div className="text-center">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
            >
              {t('auth_login_button_login')}
            </Button>
          </div>

        </form>

        </>
        )}

        {/* Forgot / Sign up */}
        <div className="flex items-center justify-between mt-5 sm:mt-8 mb-3">
          <Link
            to="/forgot-password"
            className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
          >
            {t('auth_login_forgot_password')}
          </Link>
          <Link
            to="/register"
            className="text-[11px] sm:text-[12px] text-bs-secondary hover:text-secondary-600 font-semibold transition-colors"
          >
            {t('auth_create_account')}
          </Link>
        </div>

        {/* Footer — stuck to bottom of card */}
        {/* eslint-disable i18next/no-literal-string */}
        <div className="mt-auto border-t border-gray-200" />
        <p className="pt-3 text-[10px] sm:text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Made with <span className="text-bs-secondary">❤️</span> by{' '}
          <a href="https://overw8.de" target="_blank" rel="noopener noreferrer" className="text-bs-secondary hover:text-secondary-600 font-medium transition-colors">OVERW8</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/house-rules/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Terms</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/packages-pricing/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Pricing</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/about/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">About</a>
        </p>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
    </div>
  );
};
